import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { ultraadminApi } from '@/services/api/ultraadmin'
import { setTokens } from '@/utils/tokenStorage'
import { ROLE_LABELS, type Role } from '@/types/role'
import { ROLE_DASHBOARD } from '@/utils/talkWelt'

export function Header() {
  const user = useAuthStore((s) => s.user)
  const switchRole = useAuthStore((s) => s.switchRole)
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [rolesOpen, setRolesOpen] = useState(false)
  const acting = Boolean(user?.acting_role || user?.acting_teacher_id || user?.acting_user_id || user?.acting_partner_company_id)
  const readOnly = Boolean(user?.acting_read_only)
  const role = (user?.acting_role || user?.role) as Role | undefined
  const roleText = role ? (ROLE_LABELS[role] ?? role) : 'Rolle'
  const name = user?.acting_display_name || user?.acting_teacher_name || ''
  const place = user?.acting_school_name || user?.acting_partner_name || ''
  const available = user?.acting_available_roles ?? []

  const stop = async () => {
    setBusy(true)
    try {
      const res = await ultraadminApi.clearActAs()
      setTokens(res.access_token, res.refresh_token)
      useAuthStore.setState({ user: res.user, isAuthenticated: true })
      navigate('/ultraadmin/dashboard', { replace: true })
    } catch {
      setBusy(false)
    }
  }

  const changeRole = async (next: Role) => {
    if (next === role || busy) return
    setBusy(true)
    setRolesOpen(false)
    try {
      await switchRole(next)
      navigate(ROLE_DASHBOARD[next] || '/dashboard', { replace: true })
    } finally {
      setBusy(false)
    }
  }

  if (!acting) return null

  const label = name
    ? `Als ${roleText}: ${name}${place ? ` · ${place}` : ''}${readOnly ? ' (nur schauen)' : ''}`
    : `Als ${roleText}${place ? ` · ${place}` : ''} (nur schauen)`

  return (
    <header className="h-14 bg-amber-50 border-b border-amber-200 flex items-center px-4 justify-between gap-3 shrink-0">
      <p className="text-sm text-amber-900 truncate">{label}</p>
      <div className="flex items-center gap-2 shrink-0 relative">
        {available.length > 1 && (
          <div className="relative">
            <button
              type="button"
              className="text-sm text-amber-900 underline disabled:opacity-50"
              disabled={busy}
              onClick={() => setRolesOpen((v) => !v)}
            >
              Rolle wechseln
            </button>
            {rolesOpen && (
              <div className="absolute right-0 top-full mt-1 min-w-[160px] bg-white border border-amber-200 rounded shadow z-50">
                {available.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50"
                    onClick={() => void changeRole(r)}
                  >
                    {ROLE_LABELS[r] ?? r}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          className="text-sm text-amber-900 underline"
          disabled={busy}
          onClick={() => navigate('/ultraadmin/act-as')}
        >
          Andere Person
        </button>
        <button
          type="button"
          className="text-sm px-2 py-1 rounded bg-amber-900 text-white disabled:opacity-50"
          disabled={busy}
          onClick={() => void stop()}
        >
          Beenden
        </button>
      </div>
    </header>
  )
}
